import type { ProjectGraphNode } from '@avisoma/shared';
import type { GroupKey } from './graphModel';
import { nodeLabel } from './nodeLabel';

/**
 * How a group's member list is ordered. `connections` keeps the group's own order (best-connected first); the
 * others sort on a field, and members that tie keep that order.
 */
export type SortMode = 'connections' | 'name' | 'partNumber' | 'authority';

/** The kind of node a group holds, which picks its sort modes; groups of one kind share the chosen mode. */
export type MemberKind = 'engine' | 'aircraft' | 'part' | 'event' | 'reference' | 'document';

const SORT_LABELS: Record<SortMode, string> = {
  connections: 'By connections',
  name: 'By name',
  partNumber: 'By part number',
  authority: 'By authority',
};

export function memberKindOf(key: GroupKey): MemberKind {
  if (key.startsWith('ref:')) return 'reference';
  if (key.startsWith('document:')) return 'document';
  return key as MemberKind;
}

/**
 * The sort modes a group offers, each with its label: connections and name for every group, plus part number for
 * parts and authority for references. A document group holds one category, so it has no category mode.
 */
export function sortModesOf(kind: MemberKind): { mode: SortMode; label: string }[] {
  const modes: SortMode[] = ['connections', 'name'];
  if (kind === 'part') modes.push('partNumber');
  if (kind === 'reference') modes.push('authority');
  return modes.map((mode) => ({ mode, label: SORT_LABELS[mode] }));
}

// Numbers in text compare as numbers (AD 2021-9 before AD 2021-10), and case does not count.
const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
const text = (a: string | undefined, b: string | undefined) =>
  a === b ? 0 : a === undefined ? 1 : b === undefined ? -1 : collator.compare(a, b);

const partNumberOf = (n: ProjectGraphNode) => (n.kind === 'part' ? n.partNumber : undefined);
const serialNumberOf = (n: ProjectGraphNode) => (n.kind === 'part' ? n.serialNumber : undefined);
const authorityOf = (n: ProjectGraphNode) => (n.kind === 'reference' ? n.authority : undefined);
const numberOf = (n: ProjectGraphNode) => (n.kind === 'reference' ? n.number : undefined);

const COMPARE: Record<Exclude<SortMode, 'connections'>, (a: ProjectGraphNode, b: ProjectGraphNode) => number> = {
  name: (a, b) => text(nodeLabel(a), nodeLabel(b)),
  partNumber: (a, b) => text(partNumberOf(a), partNumberOf(b)) || text(serialNumberOf(a), serialNumberOf(b)),
  // References without an authority go last.
  authority: (a, b) => text(authorityOf(a), authorityOf(b)) || text(numberOf(a), numberOf(b)),
};

/** The members in `mode` order; `connections` returns them as given. A new array either way. */
export function sortMembers(nodes: readonly ProjectGraphNode[], mode: SortMode): ProjectGraphNode[] {
  return mode === 'connections' ? [...nodes] : [...nodes].sort(COMPARE[mode]);
}

/** The distinct authorities of the reference members, sorted. */
export function authoritiesOf(nodes: readonly ProjectGraphNode[]): string[] {
  const found = new Set(nodes.flatMap((n) => (n.kind === 'reference' && n.authority ? [n.authority] : [])));
  return [...found].sort(collator.compare);
}

/** The members issued by `authority`; all of them when it is `null`. */
export function withAuthority(nodes: readonly ProjectGraphNode[], authority: string | null): readonly ProjectGraphNode[] {
  return authority === null ? nodes : nodes.filter((n) => n.kind === 'reference' && n.authority === authority);
}
